import { toDollars } from '@/util/currency';
import { CustomGoal } from '@common/goals';
import { getGoalBaseConfig } from '@common/react/goals';
import { dt } from '@common/util/dateTime';
import { capitalize } from '@common/util/strings';
import { isNumber, isString } from '@common/util/types';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    deriveTransactionSchedulePlan,
    getFinalAssumptionsWithRisk,
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

export const CUSTOM: GoalConfig<CustomGoal> = {
    inputSteps: [],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (
            !isNumber(goalData.targetAmount) ||
            !isString(goalData.targetDate)
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = CUSTOM.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('targetAmount')}
                    color={color}
                />
                {` by `}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {`.`}
            </>
        );
    },
    getAssumptionsContent: ({ goalData, userInfo, currentRateInfo }) =>
        getFinalAssumptionsWithRisk({
            goalData,
            recommendedRiskScore: CUSTOM.getRecommendedRiskScore({
                goalData,
                userInfo,
            }),
            currentRateInfo,
        }),
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
        currentRateInfo,
    }): Promise<GoalData<CustomGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        // TODO: Refactor this to handle the actual risk score on a goal account
        // instead of using the onboarding risk score forever.
        const riskScore = newGoalData.riskScore;
        const annualReturn = CUSTOM.getAnnualReturn({
            investmentType:
                riskScore === 0
                    ? 'HIGH_YIELD_CASH'
                    : newGoalData.investmentType,
            riskScore,
            currentRateInfo,
        });
        const transactionSchedulePlan =
            isNumber(newGoalData.targetAmount) && isString(targetDate)
                ? deriveTransactionSchedulePlan(
                      {
                          annualReturn,
                          targetAmount: newGoalData.targetAmount,
                          targetDate,
                          initialDepositPlan: 0,
                          currentRateInfo,
                      },
                      { resetStartDate: true, clampToTargetAmount: true }
                  )
                : undefined;

        return {
            ...newGoalData,
            targetDate,
            // TODO: Update this to support multiplayer
            primaryTransactionSchedulePlan: transactionSchedulePlan,
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('CUSTOM'),
};
